(() => {
  const SECTION_SELECTOR = '[data-main-product-root]';
  const STORAGE_KEY = 'zero_abandon_checkout_state_v1';
  const MAX_AGE_MS = 45 * 60 * 1000;
  const VIDEO_INIT_KEY = '__mainProductVideoPopupInit';
  const SCROLL_TOP_INIT_KEY = '__zeroProductTabsScrollTopInit';
  const SOCIAL_PROOF_INIT = 'data-social-proof-init';

  const isCheckoutReferrer = (ref) => /\/checkouts?\//i.test(ref) || /checkout\.shopify\.com/i.test(ref);
  const isOrderCompleteReferrer = (ref) => /thank_you|order_status|\/orders\//i.test(ref);

  const readState = () => {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  };

  const writeState = (state) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {}
  };

  const clearState = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {}
  };

  const initVideoPopup = () => {
    if (window[VIDEO_INIT_KEY]) return;
    window[VIDEO_INIT_KEY] = true;

    const videoPreview = document.getElementById('videoPreview');
    const videoPopup = document.getElementById('videoPopup');
    const popupVideo = document.getElementById('popupVideo');
    const closeButton = document.querySelector('.close-button');
    const body = document.body;
    if (!videoPreview || !videoPopup || !popupVideo || !closeButton) return;

    const closePopup = () => {
      videoPopup.style.display = 'none';
      body.classList.remove('no-scroll');
      popupVideo.pause();
      popupVideo.currentTime = 0;
    };

    videoPreview.addEventListener('click', () => {
      videoPopup.style.display = 'flex';
      body.classList.add('no-scroll');
      popupVideo.play();
    });

    closeButton.addEventListener('click', closePopup);
    videoPopup.addEventListener('click', (event) => {
      if (event.target === videoPopup) {
        closePopup();
      }
    });
  };

  const initCountdown = (sectionRoot, sectionElement) => {
    if (sectionRoot.dataset.countdownInit === 'true') return;
    sectionRoot.dataset.countdownInit = 'true';

    const endDate = sectionElement.dataset.countdownEndDate || '';
    const intervalMinutes = Number.parseInt(sectionElement.dataset.countdownInterval || '', 10);
    const finalEndTime = new Date(endDate).getTime();

    const hourElements = sectionRoot.querySelectorAll('.hoursa');
    const minuteElements = sectionRoot.querySelectorAll('.minutesa');
    const secondElements = sectionRoot.querySelectorAll('.secondsa');

    if (!hourElements.length || !minuteElements.length || !secondElements.length) return;
    if (!Number.isFinite(finalEndTime) || !Number.isFinite(intervalMinutes) || intervalMinutes <= 0) return;

    let interval = null;
    let lastRendered = '';

    const getNextIntervalEndTime = (now) => {
      const intervalMillis = intervalMinutes * 60 * 1000;
      return Math.ceil(now / intervalMillis) * intervalMillis;
    };

    const render = (h, m, s) => {
      hourElements.forEach((el) => { el.textContent = h; });
      minuteElements.forEach((el) => { el.textContent = m; });
      secondElements.forEach((el) => { el.textContent = s; });
    };

    const stopInterval = () => {
      if (!interval) return;
      clearInterval(interval);
      interval = null;
    };

    const updateCountdown = () => {
      const now = Date.now();
      const nextIntervalEndTime = getNextIntervalEndTime(now);

      if (now >= finalEndTime) {
        stopInterval();
        if (lastRendered !== '00:00:00') {
          render('00', '00', '00');
          lastRendered = '00:00:00';
        }
        return;
      }

      const timeLeft = nextIntervalEndTime - now;
      const hours = Math.floor(timeLeft / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
      const nextRendered = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

      if (nextRendered === lastRendered) return;
      const [h, m, s] = nextRendered.split(':');
      render(h, m, s);
      lastRendered = nextRendered;
    };

    const startInterval = () => {
      if (interval || document.hidden) return;
      interval = setInterval(updateCountdown, 1000);
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
        return;
      }
      updateCountdown();
      startInterval();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', () => {
      stopInterval();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }, { once: true });

    updateCountdown();
    startInterval();
  };

  const initAbandonCheckout = (sectionRoot, sectionElement) => {
    if (sectionRoot.dataset.abandonCheckoutInit === 'true') return;
    sectionRoot.dataset.abandonCheckoutInit = 'true';

    const productId = sectionElement.dataset.productId || '';
    const checkoutPath = sectionElement.dataset.checkoutPath || '/checkout';
    const modal = sectionRoot.querySelector('[data-abandon-checkout-modal]');
    if (!modal) return;

    const closeBtn = modal.querySelector('[data-abandon-close]');
    const dismissBtn = modal.querySelector('[data-abandon-dismiss]');
    const continueBtn = modal.querySelector('[data-abandon-continue]');
    if (!closeBtn || !dismissBtn || !continueBtn) return;

    const checkoutReferrerUrl =
      isCheckoutReferrer(document.referrer) && !isOrderCompleteReferrer(document.referrer)
        ? document.referrer
        : '';
    const resumeCheckoutUrl = checkoutReferrerUrl || checkoutPath;
    let activeCheckoutState = null;

    const closeModal = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('abandon-checkout-open');
      clearState();
      activeCheckoutState = null;
    };

    const openModal = () => {
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('abandon-checkout-open');
    };

    const markCheckoutStarted = (variantId) => {
      writeState({
        productId,
        variantId: variantId || null,
        startedAt: Date.now()
      });
    };

    const getVariantIdFromElement = (element) => {
      const form = element ? element.closest('form') : null;
      const variantInput = form ? form.querySelector('input[name="id"]') : null;
      return variantInput ? variantInput.value : null;
    };

    const bindBuyNowTracking = () => {
      const bindIframeTracking = () => {
        const iframes = sectionRoot.querySelectorAll('.shopify-payment-button iframe');
        iframes.forEach((iframe) => {
          if (iframe.dataset.abandonTracked === 'true') return;
          iframe.dataset.abandonTracked = 'true';
          iframe.addEventListener('pointerdown', () => markCheckoutStarted(getVariantIdFromElement(iframe)), true);
          iframe.addEventListener('mousedown', () => markCheckoutStarted(getVariantIdFromElement(iframe)), true);
          iframe.addEventListener('focus', () => markCheckoutStarted(getVariantIdFromElement(iframe)), true);
          iframe.addEventListener('touchstart', () => markCheckoutStarted(getVariantIdFromElement(iframe)), true);
        });
      };

      sectionRoot.addEventListener('click', (event) => {
        const checkoutTrigger = event.target.closest('button[name="checkout"], .shopify-payment-button__button');
        if (!checkoutTrigger) return;
        markCheckoutStarted(getVariantIdFromElement(checkoutTrigger));
      }, true);

      sectionRoot.addEventListener('pointerdown', (event) => {
        const checkoutArea = event.target.closest('.shopify-payment-button, shopify-accelerated-checkout');
        if (!checkoutArea) return;
        markCheckoutStarted(getVariantIdFromElement(checkoutArea));
      }, true);

      sectionRoot.addEventListener('focusin', (event) => {
        const checkoutArea = event.target.closest('.shopify-payment-button, shopify-accelerated-checkout');
        if (!checkoutArea) return;
        markCheckoutStarted(getVariantIdFromElement(checkoutArea));
      }, true);

      sectionRoot.addEventListener('submit', (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        const submitter = event.submitter;
        const isCheckoutSubmitter =
          submitter && (submitter.getAttribute('name') === 'checkout' || submitter.classList.contains('shopify-payment-button__button'));
        const hasAcceleratedButton = Boolean(form.querySelector('.shopify-payment-button, shopify-accelerated-checkout'));
        if (!isCheckoutSubmitter && !hasAcceleratedButton) return;
        const variantInput = form.querySelector('input[name="id"]');
        markCheckoutStarted(variantInput ? variantInput.value : null);
      }, true);

      bindIframeTracking();
      let rafId = null;
      const paymentObserver = new MutationObserver(() => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          bindIframeTracking();
        });
      });

      paymentObserver.observe(sectionRoot, { childList: true, subtree: true });
      window.addEventListener('pagehide', () => {
        paymentObserver.disconnect();
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
      }, { once: true });
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(bindBuyNowTracking, { timeout: 1200 });
    } else {
      setTimeout(bindBuyNowTracking, 300);
    }

    const evaluateAndShowModal = (pageWasRestored) => {
      const state = readState();
      const isRecentStart = state && state.startedAt && Date.now() - Number(state.startedAt) < MAX_AGE_MS;
      const sameProduct = state && String(state.productId) === String(productId);
      const navEntry = performance.getEntriesByType('navigation')[0];
      const isBackForward = navEntry && navEntry.type === 'back_forward';
      const isBackLikeNavigation = Boolean(isBackForward || pageWasRestored);
      const hasValidState = Boolean(state && isRecentStart && sameProduct);
      const checkoutReturn = isCheckoutReferrer(document.referrer) && !isOrderCompleteReferrer(document.referrer);
      const shouldShow = Boolean((checkoutReturn && (hasValidState || !state)) || (hasValidState && isBackLikeNavigation));

      if (!shouldShow) return;
      activeCheckoutState = hasValidState ? state : null;
      openModal();
    };

    evaluateAndShowModal(false);
    window.addEventListener('pageshow', (event) => {
      evaluateAndShowModal(Boolean(event.persisted));
    });

    closeBtn.addEventListener('click', closeModal);
    dismissBtn.addEventListener('click', (event) => {
      event.preventDefault();

      const variantFromState =
        activeCheckoutState && activeCheckoutState.variantId
          ? String(activeCheckoutState.variantId)
          : '';
      const variantInput = sectionRoot.querySelector('form input[name="id"]');
      const variantId = variantFromState || (variantInput ? String(variantInput.value || '') : '');

      const directResumeUrl =
        resumeCheckoutUrl && resumeCheckoutUrl !== window.location.href
          ? resumeCheckoutUrl
          : '';

      const fallbackCheckoutUrl = variantId
        ? `/cart/${encodeURIComponent(variantId)}:1?checkout`
        : checkoutPath;

      dismissBtn.disabled = true;
      window.location.href = directResumeUrl || fallbackCheckoutUrl;

      setTimeout(() => {
        closeModal();
      }, 300);
    });

    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    });
    continueBtn.addEventListener('click', closeModal);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });
  };

  const initSocialProofViewed = () => {
    document.querySelectorAll('[data-product-social-proof-viewed]').forEach((root) => {
      if (root.getAttribute(SOCIAL_PROOF_INIT) === 'true') return;

      const countEl = root.querySelector('[data-people-viewed-count]');
      const productHandle = root.dataset.productHandle;
      if (!countEl || !productHandle) return;

      root.setAttribute(SOCIAL_PROOF_INIT, 'true');

      const storageKey = `viewed_count_${productHandle}`;
      const now = Date.now();
      const weekMs = 7 * 24 * 60 * 60 * 1000;
      let stored = null;

      try {
        stored = JSON.parse(localStorage.getItem(storageKey) || 'null');
      } catch (error) {
        stored = null;
      }

      if (!stored || typeof stored.timestamp !== 'number' || now - stored.timestamp > weekMs) {
        const viewedCount = Math.floor(Math.random() * (2000 - 1000 + 1)) + 1000;
        try {
          localStorage.setItem(storageKey, JSON.stringify({ count: viewedCount, timestamp: now }));
        } catch (error) {}
        countEl.textContent = String(viewedCount);
      } else {
        countEl.textContent = String(stored.count);
      }
    });
  };

  const initProductTabsScrollTop = () => {
    if (window[SCROLL_TOP_INIT_KEY]) return;

    const scrollToTopBtn = document.getElementById('scrollToTopBtn');
    if (!scrollToTopBtn) return;

    window[SCROLL_TOP_INIT_KEY] = true;

    let lastScrollTop = 0;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const isScrollingUp = scrollTop < lastScrollTop;
      lastScrollTop = scrollTop;

      if (scrollTop > 600 && isScrollingUp) {
        scrollToTopBtn.classList.add('show');
      } else {
        scrollToTopBtn.classList.remove('show');
      }
    };

    const scrollToTop = () => {
      const media = document.querySelector('.product__media');
      if (media) {
        media.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    scrollToTopBtn.addEventListener('click', scrollToTop);
  };

  const initActiveOffers = () => {
    const INIT_KEY = '__zeroActiveOffersInit';
    if (window[INIT_KEY]) return;
    window[INIT_KEY] = true;

    const getShopRoot = () => {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return window.Shopify.routes.root;
      }
      if (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.rootUrl) {
        return window.themeVariables.routes.rootUrl;
      }
      return '/';
    };

    const getProductQuantityInput = (form) => {
      if (form) {
        const input = form.querySelector('[name="quantity"]');
        if (input) return input;
      }
      return document.querySelector('.product-form [name="quantity"]');
    };

    const setProductQuantity = (qty, form) => {
      const input = getProductQuantityInput(form);
      if (!input) return;
      input.value = qty;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const highlightOfferCard = (qty) => {
      const offersEl = document.querySelector('[data-offer-auto-apply]');
      if (!offersEl) return;

      const offer2Qty = parseInt(offersEl.getAttribute('data-offer-2-qty') || '0', 10);
      const offer1Qty = parseInt(offersEl.getAttribute('data-offer-1-qty') || '0', 10);
      let selectedQty = null;

      if (qty >= offer2Qty) {
        selectedQty = offer2Qty;
      } else if (qty >= offer1Qty) {
        selectedQty = offer1Qty;
      }

      document.querySelectorAll('.active-offers__card').forEach((card) => {
        card.classList.toggle(
          'is-selected',
          selectedQty !== null && card.getAttribute('data-offer-qty') === String(selectedQty)
        );
      });
    };

    const resolveDiscountCode = (offersEl, quantity) => {
      if (!offersEl) return '';

      const offer2Qty = parseInt(offersEl.getAttribute('data-offer-2-qty') || '0', 10);
      const offer1Qty = parseInt(offersEl.getAttribute('data-offer-1-qty') || '0', 10);
      const offer2Code = (offersEl.getAttribute('data-offer-2-code') || '').trim();
      const offer1Code = (offersEl.getAttribute('data-offer-1-code') || '').trim();

      if (quantity >= offer2Qty && offer2Code) return offer2Code;
      if (quantity >= offer1Qty && offer1Code) return offer1Code;
      return '';
    };

    const applyDiscountCode = async (code) => {
      if (!code) return;

      const root = getShopRoot();
      try {
        await fetch(`${root}discount/${encodeURIComponent(code)}`, {
          method: 'GET',
          credentials: 'same-origin',
        });
      } catch (error) {}
    };

    const addOfferToCart = (qty, form) => {
      if (!form) return;
      const submitButton = form.querySelector('[data-product-add-to-cart-button]')
        || form.querySelector('button[type="submit"]');

      if (submitButton && !submitButton.hasAttribute('disabled')) {
        submitButton.click();
      } else if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    };

    document.addEventListener('click', (event) => {
      const card = event.target.closest('[data-offer-qty]');
      if (!card) return;

      const qty = parseInt(card.getAttribute('data-offer-qty') || '1', 10);
      const form = card.closest('[data-main-product-root]')?.querySelector('form[is="product-form"]')
        || document.querySelector('form[is="product-form"]');

      setProductQuantity(qty, form);
      highlightOfferCard(qty);
      addOfferToCart(qty, form);
    });

    document.documentElement.addEventListener('variant:added', (event) => {
      const form = event.target && event.target.closest
        ? event.target.closest('form[is="product-form"]')
        : document.querySelector('form[is="product-form"]');
      const offersEl = document.querySelector('[data-offer-auto-apply]');
      const quantityInput = getProductQuantityInput(form);
      const quantity = parseInt(quantityInput?.value || '1', 10);
      const code = resolveDiscountCode(offersEl, quantity);

      if (code) {
        applyDiscountCode(code);
      }
    });

    const quantityInput = document.querySelector('.product-form [name="quantity"]');
    if (quantityInput) {
      quantityInput.addEventListener('change', () => {
        highlightOfferCard(parseInt(quantityInput.value || '1', 10));
      });
    }
  };

  const scheduleDeferredProductUi = () => {
    const run = () => {
      initSocialProofViewed();
      initProductTabsScrollTop();
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 150);
    }
  };

  const initSection = (sectionElement) => {
    const sectionRoot = sectionElement.closest('[id^="shopify-section-"]');
    if (!sectionRoot) return;

    initCountdown(sectionRoot, sectionElement);
    initAbandonCheckout(sectionRoot, sectionElement);
  };

  const initAll = () => {
    initVideoPopup();
    initActiveOffers();
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
    scheduleDeferredProductUi();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const container = event && event.target ? event.target : document;
    container.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
    initVideoPopup();
    initSocialProofViewed();
  });
})();
